import React, { memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Button,
  Icon,
  TextField,
  Select,
  MenuItem,
  Typography,
  Card,
  Checkbox,
  Divider,
  InputAdornment,
  IconButton
} from 'investira.react.components';
import { BaseTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import { AVALIACAO_SESSOES_QUESTOES } from '../../../enums';
import services from '../../../services';

const ALFABETO_OPTIONS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const AvaliacoesSettingsSecaoAdd = memo(props => {
  const navigate = useNavigate();
  const xSessoesQuestoes = Object.values(AVALIACAO_SESSOES_QUESTOES);
  const xQuestoes = useMemo(() => {
    return xSessoesQuestoes.flatMap(xSessao =>
      xSessao.questoes.map(xQuestao => ({
        ...xQuestao,
        sessao_label: xSessao.sessao
      }))
    );
  }, [xSessoesQuestoes]);

  const [titulo, setTitulo] = useState('');
  const [alias, setAlias] = useState('');
  const [descricao, setDescricao] = useState('');
  const [buscaQuestao, setBuscaQuestao] = useState('');
  const [questoesSelecionadas, setQuestoesSelecionadas] = useState({});

  const xTotalQuestoes = xQuestoes.length;

  const xQuestoesFiltradas = useMemo(() => {
    const xBuscaNormalizada = buscaQuestao.trim().toLowerCase();

    return xQuestoes.filter(xQuestao => {
      if (!xBuscaNormalizada) {
        return true;
      }

      return xQuestao.questao.toLowerCase().includes(xBuscaNormalizada);
    });
  }, [buscaQuestao, xQuestoes]);

  const xTotalSelecionadas = Object.keys(questoesSelecionadas).length;

  const isQuestaoSelecionada = pQuestaoId => {
    return Boolean(questoesSelecionadas[pQuestaoId]);
  };

  const handleToggleQuestao = pQuestao => {
    setQuestoesSelecionadas(pCurrent => {
      const xIsSelected = Boolean(pCurrent[pQuestao.id]);

      if (xIsSelected) {
        const xNewValue = { ...pCurrent };
        delete xNewValue[pQuestao.id];
        return xNewValue;
      }

      return {
        ...pCurrent,
        [pQuestao.id]: {
          id: pQuestao.id,
          questao: pQuestao.questao,
          sessao_id: pQuestao.sessao_id
        }
      };
    });
  };

  const handleSelectTodasFiltradas = () => {
    setQuestoesSelecionadas(pCurrent => {
      const xAllSelected =
        xQuestoesFiltradas.length > 0 &&
        xQuestoesFiltradas.every(xQuestao => Boolean(pCurrent[xQuestao.id]));

      if (xAllSelected) {
        const xNewValue = { ...pCurrent };
        xQuestoesFiltradas.forEach(xQuestao => {
          delete xNewValue[xQuestao.id];
        });
        return xNewValue;
      }

      const xNewValue = { ...pCurrent };
      xQuestoesFiltradas.forEach(xQuestao => {
        if (!xNewValue[xQuestao.id]) {
          xNewValue[xQuestao.id] = {
            id: xQuestao.id,
            questao: xQuestao.questao,
            sessao_id: xQuestao.sessao_id
          };
        }
      });
      return xNewValue;
    });
  };

  const handleSave = async () => {
    if (!titulo.trim()) {
      props.onMessageError?.('Informe o título da sessão.');
      return;
    }

    if (!alias.trim()) {
      props.onMessageError?.('Informe o alias da sessão.');
      return;
    }

    if (xTotalSelecionadas === 0) {
      props.onMessageError?.('Selecione ao menos uma questão para a seção.');
      return;
    }

    const xPayload = {
      titulo: titulo.trim(),
      alias: alias.trim(),
      descricao: descricao.trim(),
      qtd_questoes: xTotalSelecionadas,
      questoes: Object.values(questoesSelecionadas)
        .sort((a, b) => a.id - b.id)
        .map(xQuestao => xQuestao.id)
    };

    try {
      const rRes = await services.mockAvaliacoes.secoesRepo.create(xPayload);

      if (!rRes.ok) {
        props.onMessageError?.(rRes?.error?.message || 'Não foi possível criar a seção.');
        return;
      }

      props.onMessageSuccess?.('Seção criada com sucesso.');
      navigate('/avaliacoes/configuracoes/secoes');
    } catch (error) {
      props.onMessageError?.('Não foi possível criar a seção.');
    }
  };

  return (
    <BaseTemplate
      title={'Nova Seção'}
      subtitle={'Configurações'}
      description="Informe o objetivo e a finalidade desta sessão para organizar e estruturar melhor os critérios da avaliação."
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2}>
          <Button
            disabled={props.disabledButtons}
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />}
            size="small"
            sx={{ px: 2 }}
            onClick={handleSave}>
            Salvar
          </Button>
        </Stack>
      }>
      <Stack gap={2} py={3}>
        <Stack direction="row" gap={2} alignItems="flex-start">
          <Stack flex={1}>
            <TextField
              label="Título"
              placeholder="Ex.: Análise Fundamentalista"
              value={titulo}
              onChange={pEvent => setTitulo(pEvent.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
            />
          </Stack>

          <Stack sx={{ width: '120px' }}>
            <Select
              value={alias}
              onChange={pEvent => setAlias(pEvent.target.value)}
              fullWidth
              displayEmpty>
              <MenuItem value="" disabled>
                Alias
              </MenuItem>
              {ALFABETO_OPTIONS.map(xLetra => {
                return (
                  <MenuItem key={xLetra} value={xLetra}>
                    {xLetra}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
        </Stack>

        <TextField
          label="Descrição"
          placeholder="Descreva o objetivo desta sessão"
          value={descricao}
          onChange={pEvent => setDescricao(pEvent.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          multiline
          rows={4}
        />
      </Stack>

      <Divider />

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={3} py={3}>
        <Stack flexGrow={1} gap={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5}>
            <TextField
              label="Buscar questão"
              placeholder="Busque por palavra-chave no enunciado"
              value={buscaQuestao}
              onChange={pEvent => setBuscaQuestao(pEvent.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon iconName="find" size={21} color={'secondaryLightness'} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size={'small'}
                      onClick={() => setBuscaQuestao('')}
                      disabled={!buscaQuestao.trim()}
                      color={'primary'}>
                      <Icon iconName={'cancel'} size={18} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectTodasFiltradas}
              sx={{ minWidth: { md: 220 } }}>
              {xQuestoesFiltradas.length > 0 &&
              xQuestoesFiltradas.every(xQuestao => isQuestaoSelecionada(xQuestao.id))
                ? 'Limpar filtradas'
                : 'Selecionar filtradas'}
            </Button>
          </Stack>

          {xQuestoesFiltradas.length === 0 && (
            <Card>
              <Stack py={3} px={2}>
                <Typography variant="body2" color="textSecondary">
                  Nenhuma questão encontrada para o filtro aplicado.
                </Typography>
              </Stack>
            </Card>
          )}

          {xQuestoesFiltradas.length > 0 && (
            <Card>
              <Stack p={2} gap={2}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  gap={1}>
                  <Typography variant="h6" color="textPrimary">
                    Questões
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {
                      xQuestoesFiltradas.filter(xQuestao => isQuestaoSelecionada(xQuestao.id))
                        .length
                    }{' '}
                    de {xQuestoesFiltradas.length} selecionadas
                  </Typography>
                </Stack>

                <Divider />

                <Stack gap={1.5}>
                  {xQuestoesFiltradas.map(xQuestao => {
                    const xSelecionada = isQuestaoSelecionada(xQuestao.id);

                    return (
                      <Stack
                        key={xQuestao.id}
                        gap={1}
                        sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 3 }}>
                        <Stack direction="row" gap={1} alignItems="center" flexGrow={1}>
                          <Checkbox
                            checked={xSelecionada}
                            onChange={() => handleToggleQuestao(xQuestao)}
                            size="small"
                            sx={{ mt: 0.2 }}
                          />
                          <Stack direction="row" justifyContent="space-between" flexGrow={1}>
                            <Stack>
                              <Typography color="textPrimary" variant="body2">
                                {xQuestao.questao}
                              </Typography>
                            </Stack>
                            <Typography color="textSecondary" variant="caption">
                              ID #{xQuestao.id}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </Card>
          )}
        </Stack>

        <Stack
          minWidth={{ lg: 280 }}
          maxWidth={{ lg: 320 }}
          width="100%"
          gap={2}
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 16 },
            alignSelf: { lg: 'flex-start' }
          }}>
          <Card>
            <Stack p={2} gap={1}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ textTransform: 'uppercase' }}>
                Resumo da seção
              </Typography>
              <Typography variant="body2" color="textPrimary">
                Questões selecionadas: {xTotalSelecionadas}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total disponível: {xTotalQuestoes}
              </Typography>
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Button
              disabled={props.disabledButtons}
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="upload" size={16} />}
              size="small"
              sx={{ px: 2 }}
              onClick={handleSave}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BaseTemplate>
  );
});

AvaliacoesSettingsSecaoAdd.displayName = 'AvaliacoesSettingsSecaoAdd';

export default withMessage(AvaliacoesSettingsSecaoAdd);
